import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, CardBody, Col, Input, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { ErrorToaster, successToaster } from "components/common/toast";
import {
  AcceptOrderAPI,
  GetOrdersAPI,
  RejectOrderAPI,
  SetOrderStatusAPI,
} from "network/orders";
import { errText } from "views/bookings/Agenda";

// Il nome dello stato si traduce in render: orders:live.status.<stato>
export const STATUS = {
  pending: { cls: "warning" },
  accepted: { cls: "info" },
  preparing: { cls: "info" },
  ready: { cls: "success" },
  out_for_delivery: { cls: "success" },
  completed: { cls: "secondary" },
  rejected: { cls: "secondary" },
  cancelled: { cls: "secondary" },
  missed: { cls: "danger" },
};
const euro = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;
const locale = () => (i18n.language === "en" ? "en-GB" : "it-IT");
const hm = (iso) => (iso ? new Date(iso).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" }) : "");
const dayHm = (iso) =>
  iso ? new Date(iso).toLocaleString(locale(), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
const MINUTES = [15, 30, 45, 60];

const OrderCard = ({ o, onAction }) => {
  const { t } = useTranslation("orders");
  const [minutes, setMinutes] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const stKey = STATUS[o.status] ? o.status : "pending";
  const st = STATUS[stKey];
  const delivery = o.kind === "delivery";
  return (
    <Card className="order-card">
      <CardBody>
        <Row>
          <Col md="4">
            <span className={`badge badge-${st.cls}`}>{t(`live.status.${stKey}`)}</span>{" "}
            <span className="badge badge-secondary">{delivery ? t("live.delivery") : t("live.pickup")}</span>
            <h4 className="mt-2 mb-0">{o.name}</h4>
            <div className="small text-muted">{o.phone} · #{o.code}</div>
            {delivery && <div className="small mt-1">{o.address}, {o.postcode} {o.city}</div>}
            <div className="small mt-1">
              {o.requested_for ? t("live.scheduledFor", { when: dayHm(o.requested_for) }) : t("live.receivedAt", { time: hm(o.created_at) })}
              {o.promised_at && ` · ${t("live.promisedFor", { time: hm(o.promised_at) })}`}
            </div>
            {o.notes && <div className="small mt-1"><em>{t("live.notes")} {o.notes}</em></div>}
          </Col>
          <Col md="5">
            {o.items.map((i, n) => (
              <div key={n}>
                <strong>{i.quantity}×</strong> {i.name} <span className="text-muted">{euro(i.line_total)}</span>
                {i.notes && <div className="small text-muted">{i.notes}</div>}
              </div>
            ))}
            {delivery && <div className="small text-muted mt-1">{t("live.deliveryFee")} {euro(o.delivery_fee)}</div>}
            {o.discount > 0 && <div className="small text-muted">{t("live.discount", { name: o.promo_name })}: −{euro(o.discount)}</div>}
            <div className="mt-2"><strong>{t("live.total")} {euro(o.total)}</strong> · {t(`live.pay.${o.payment_method}`, { defaultValue: "" })}</div>
            {o.reject_reason && <div className="small">{t("live.reason")} {o.reject_reason}</div>}
          </Col>
          <Col md="3" className="mt-2 mt-md-0">
            {o.status === "pending" && !rejecting && (
              <>
                <div className="small mb-1">{t("live.readyIn")}</div>
                <div className="mb-2">
                  {MINUTES.map((m) => (
                    <Button key={m} size="sm" color={String(minutes) === String(m) ? "primary" : "default"} onClick={() => setMinutes(m)}>{m}</Button>
                  ))}
                  <Input type="number" min="1" max="240" placeholder={t("live.other")} value={minutes} onChange={(e) => setMinutes(e.target.value)} style={{ width: 80, display: "inline-block" }} />
                </div>
                <Button color="success" size="sm" onClick={() => onAction(() => AcceptOrderAPI(o.id, minutes || null), t("live.accepted"))}>{t("live.accept")}</Button>
                <Button color="danger" size="sm" onClick={() => setRejecting(true)}>{t("live.reject")}</Button>
              </>
            )}
            {o.status === "pending" && rejecting && (
              <>
                <Input placeholder={t("live.reasonPlaceholder")} value={reason} onChange={(e) => setReason(e.target.value)} className="mb-2" />
                <Button color="danger" size="sm" onClick={() => onAction(() => RejectOrderAPI(o.id, reason), t("live.rejectedToast"))}>{t("live.confirmReject")}</Button>
                <Button size="sm" onClick={() => setRejecting(false)}>{t("live.back")}</Button>
              </>
            )}
            {o.status === "accepted" && <Button size="sm" color="info" onClick={() => onAction(() => SetOrderStatusAPI(o.id, "preparing"))}>{t("live.status.preparing")}</Button>}
            {["accepted", "preparing"].includes(o.status) && <Button size="sm" color="success" onClick={() => onAction(() => SetOrderStatusAPI(o.id, "ready"))}>{t("live.status.ready")}</Button>}
            {o.status === "ready" && delivery && <Button size="sm" color="success" onClick={() => onAction(() => SetOrderStatusAPI(o.id, "out_for_delivery"))}>{t("live.status.out_for_delivery")}</Button>}
            {["ready", "out_for_delivery"].includes(o.status) && <Button size="sm" color="primary" onClick={() => onAction(() => SetOrderStatusAPI(o.id, "completed"))}>{delivery ? t("live.delivered") : t("live.pickedUp")}</Button>}
            {["accepted", "preparing", "ready", "out_for_delivery"].includes(o.status) && (
              <Button size="sm" color="link" onClick={() => window.confirm(t("live.cancelConfirm")) && onAction(() => SetOrderStatusAPI(o.id, "cancelled"), t("live.cancelledToast"))}>{t("cancel")}</Button>
            )}
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const OrdersLive = () => {
  const { t } = useTranslation("orders");
  const [view, setView] = useState("open");
  const [orders, setOrders] = useState([]);
  const seen = useRef(null);

  const load = useCallback(async () => {
    try {
      const list = await GetOrdersAPI(view === "open" ? "open" : "all");
      setOrders(list);
      if (view === "open") {
        const pending = list.filter((o) => o.status === "pending").map((o) => o.id);
        if (seen.current && pending.some((id) => !seen.current.includes(id))) {
          successToaster(t("live.newOrder"));
        }
        seen.current = pending;
      }
    } catch (e) {
      ErrorToaster(errText(e));
    }
  }, [view, t]);
  useEffect(() => {
    seen.current = null;
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [load]);

  const act = async (fn, ok) => {
    try {
      await fn();
      if (ok) successToaster(ok);
      load();
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };

  return (
    <>
      <div className="mb-3">
        <Button size="sm" color={view === "open" ? "primary" : "default"} onClick={() => setView("open")}>{t("live.toHandle")}</Button>
        <Button size="sm" color={view === "all" ? "primary" : "default"} onClick={() => setView("all")}>{t("live.history")}</Button>
        <span className="text-muted small ml-3">{t("live.autoRefresh")}</span>
      </div>
      {orders.length === 0 && (
        <Card><CardBody><p className="text-muted mb-0">{view === "open" ? t("live.noneToHandle") : t("live.none")}</p></CardBody></Card>
      )}
      {orders.map((o) => <OrderCard key={o.id + o.status} o={o} onAction={act} />)}
    </>
  );
};

export default OrdersLive;
